import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useEffect, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { ThaiLottoResultCard } from '@/component/ResultCard/ThaiLottoResultCard'
import { LottoCountryResultCard } from '@/component/ResultCard/LottoCountryResultCard'
import { YeekeeLottoResultCard } from '@/component/ResultCard/YeekeeLottoResultCard'
import dayjs from 'dayjs'
import { DATE_FORMAT_QUERY } from '@/utils/date-time-utils'
import { RootState } from '@/redux/reducers'
import { Game } from '@/redux/reducers/gameReducer'
import { sagaActions } from '@/redux/sagaActions'
import { LottoGroupResultCard } from '@/component/ResultCard/LottoGroupResultCard'
import { trans } from '@/i18n/utils'
import LottoCard from '@/component/LottoCard'
import { ResultCardWithHistoryLink } from '@/ui-components/components/ResultHistory'

// Types and Interfaces
// --------------------
interface ResultPageHook {
  title: string
  onBackAction: () => void
  loading?: boolean
  selectedDate: string
  handleDateChange: (date: string) => void
  toggleDatePicker?: () => void
  renderers?: React.ReactNode[]
  optionsMenu?: { value: string; label: string }[]
  handleSelect: (value: string) => void
  selectedGame: string
  showDropdown: boolean
  setShowDropdown: (show: boolean) => void
  filteredGames: Game[]
  handleSearchQueryChange: (query: string) => void
}

type RenderType = keyof typeof RENDER_RESULT_MAP
type RenderConfig = (typeof RENDER_RESULT_MAP)[RenderType]

// Constants
// ---------
export const RENDER_RESULT_MAP = {
  yeekee: { type: ['yeekee', 'encrypt_game'] },
  stock: { type: ['stock'] },
  government: { type: ['default'] },
  group: { type: ['group', 'group_six'] },
} as const

/**
 * Filter games by search query or selected game ID
 */
const filterGames = (
  games: Game[],
  selectedGameId: string,
  searchQuery: string,
  i18n: typeof import('i18next').default,
): Game[] => {
  // Priority 1: Filter by search query (if present)
  if (searchQuery.trim()) {
    return games.filter((game) => {
      const gameName = trans(i18n, game.translations, 'name')
      return gameName.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }

  // Priority 2: Filter by selected game ID (if not 'all')
  if (selectedGameId && selectedGameId !== 'all') {
    return games.filter((game) => game.id.toString() === selectedGameId)
  }

  // Default: Return all games
  return [...games]
}

/**
 * Get sort rank for a game based on its status
 * Lower rank = higher priority (appears first)
 */
const getGameSortRank = (game: Game): number => {
  const firstRound = game.rounds?.[0]

  if (!firstRound) return 3 // No rounds: lowest priority

  if (firstRound.is_result_confirmed) return 0 // Confirmed results: highest priority

  const now = dayjs()
  const isClosed = firstRound.close_at
    ? now.isAfter(dayjs(firstRound.close_at))
    : false

  return isClosed ? 1 : 2 // Closed pending: high priority, Open: medium priority
}

/**
 * Sort games by status and type
 * Order: Confirmed > Closed pending > Open > No rounds
 */
const sortGamesByStatus = (games: Game[]): Game[] => {
  return games.sort((a, b) => {
    const statusDiff = getGameSortRank(a) - getGameSortRank(b)
    if (statusDiff !== 0) return statusDiff

    // If same status, sort alphabetically by type
    return a.type.localeCompare(b.type)
  })
}

// Component Renderers
// -----------------
export const renderThaiGovernmentComponent = (
  result: Game,
): React.ReactNode => {
  return (
    <ThaiLottoResultCard
      key={`thai-lotto-${result.id}`}
      game={result}
      rounds={result.rounds}
    />
  )
}

export const renderYeekeeComponent = (result: Game): React.ReactNode => {
  return (
    <YeekeeLottoResultCard
      key={`yeekee-${result.id}`}
      game={result}
      rounds={result.rounds}
    />
  )
}

export const renderStockComponent = (result: Game): React.ReactNode => {
  return (
    <LottoCountryResultCard
      key={`lotto-country-${result.id}`}
      game={result}
      rounds={result.rounds}
    />
  )
}

export const renderLottoGroupComponent = (result: Game): React.ReactNode => {
  return <LottoGroupResultCard game={result} rounds={result.rounds} />
}

const selectResults = createSelector(
  [
    (state: RootState) => state.games.results,
    (state: RootState) => state.games.loading.results,
  ],
  (results, loading) => ({ results, loading }),
)

const useResultPage = (): ResultPageHook => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedGame, setSelectedGame] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [filteredGames, setFilteredGames] = useState<Game[]>([])
  const [selectedDate, setSelectedDate] = useState<string>(
    dayjs().format(DATE_FORMAT_QUERY),
  )
  const { results, loading } = useSelector(selectResults)
  const optionsMenu = results.map((result: Game) => ({
    value: result.id.toString(),
    label: trans(i18n, result.translations, 'name'),
  }))
  optionsMenu.unshift({ value: 'all', label: t('resultPage.all') })
  const onBackAction = () => {
    navigate(-1)
  }
  const fetchPage = useCallback(() => {
    dispatch({
      type: sagaActions.FETCH_RESULTS,
      payload: { date: selectedDate },
    })
  }, [dispatch, selectedDate])
  const handleSelect = (value: string) => {
    setSelectedGame(value || 'all')
  }
  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])
  const handleDateChange = (date: string) => {
    setSelectedDate(dayjs(date).format(DATE_FORMAT_QUERY))
  }

  // Filter and sort games based on selection and search query
  useEffect(() => {
    const filtered = filterGames(results, selectedGame, searchQuery, i18n)
    const sorted = sortGamesByStatus(filtered)
    setFilteredGames(sorted)
  }, [selectedGame, results, searchQuery, i18n])

  const getRenderer = () => {
    return filteredGames
      .map((result) => {
        const latestRound = result.rounds?.[0]
        const isResultConfirmed = latestRound?.is_result_confirmed === true

        let card: React.ReactNode = null

        if (latestRound && !isResultConfirmed) {
          card = (
            <LottoCard
              key={`pending-${result.id}`}
              game={result}
              round={latestRound}
            />
          )
        } else {
          const matchedKey = (
            Object.entries(RENDER_RESULT_MAP) as [RenderType, RenderConfig][]
          ).find(([, config]) =>
            (config.type as readonly string[]).includes(result.type),
          )?.[0]

          switch (matchedKey) {
            case 'yeekee':
              card = renderYeekeeComponent(result)
              break
            case 'stock':
              card = renderStockComponent(result)
              break
            case 'government':
              card = renderThaiGovernmentComponent(result)
              break
            case 'group':
              card = renderLottoGroupComponent(result)
              break
          }
        }

        if (!card) return null

        return (
          <ResultCardWithHistoryLink
            key={`result-wrapper-${result.id}`}
            to={`/lotto/result/history/${result.id}`}
            linkState={{
              gameName: trans(i18n, result.translations, 'name'),
              gameType: result.type,
            }}
          >
            {card}
          </ResultCardWithHistoryLink>
        )
      })
      .filter((renderer) => renderer !== null)
  }

  useEffect(() => {
    fetchPage()
  }, [selectedDate, fetchPage])

  return {
    title: t('resultPage.header'),
    onBackAction,
    selectedGame,
    handleSelect,
    optionsMenu,
    showDropdown,
    setShowDropdown,
    selectedDate,
    handleDateChange,
    renderers: getRenderer(),
    filteredGames,
    loading,
    handleSearchQueryChange,
  }
}

export default useResultPage
